import {
  RemoteVideoTrack,
  RemoteAudioTrack,
  RemoteParticipant,
  RemoteTrack, RemoteDataTrack,
} from "twilio-video";
import { useCallback } from "react";
import { RemoteParticipantData } from "store/remote-participants/types";
import { useDispatch } from "react-redux";
import { updateRemoteParticipant } from "store/remote-participants/slice";
import { findAudioTrack, findVideoTracks, uuidv4 } from "utils";
import { useTracks } from "./useTracks";
import { addChatMessage } from "../store/chat-messages/slice";

export function useTrackEventHandlers(
  participant: RemoteParticipant,
  participantState: RemoteParticipantData,
  videoRef: React.RefObject<HTMLVideoElement>,
  audioRef: React.RefObject<HTMLAudioElement>
) {
  const dispatch = useDispatch();
  const { setVideoTrack, setAudioTrack } = useTracks(videoRef, audioRef);

  const update = useCallback(
    (payload: Partial<RemoteParticipantData>) => {
      dispatch(
        updateRemoteParticipant({
          sid: participant.sid,
          ...payload,
        })
      );
    },
    [dispatch, participant]
  );

  const setInitialTracks = useCallback(
    function () {
      const { screenShareTrack, cameraTrack } = findVideoTracks(participant.videoTracks);
      const audioTrack = findAudioTrack(participant.audioTracks);

      setVideoTrack(cameraTrack);
      setAudioTrack(audioTrack);

      const updateArgs: Partial<RemoteParticipantData> = {
        audioIsEnabled: !!audioTrack?.isEnabled,
        videoIsEnabled: !!cameraTrack?.isEnabled,
        isSwitchedOff: !!(cameraTrack as any)?.isSwitchedOff,
        isSharingScreen: screenShareTrack ? true : false,
      };

      update(updateArgs);
    },
    [
      participant.videoTracks,
      participant.audioTracks,
      setVideoTrack,
      setAudioTrack,
      update,
    ]
  );

  const setInitialFocusedParticipantTracks = useCallback(
    function () {
      const { screenShareTrack, cameraTrack } = findVideoTracks(participant.videoTracks);
      const audioTrack = findAudioTrack(participant.audioTracks);

      if (screenShareTrack) {
        (screenShareTrack as any).setPriority("high");
        if (!participantState.isSharingScreen) {
          update({ isSharingScreen: true });
        }
        setVideoTrack(screenShareTrack);
      } else if (cameraTrack) {
        setVideoTrack(cameraTrack);
      }

      setAudioTrack(audioTrack);
      update({
        videoIsEnabled: !!cameraTrack?.isEnabled,
        isSwitchedOff: !!(cameraTrack as any)?.isSwitchedOff,
      });
    },
    [
      participant.videoTracks,
      participant.audioTracks,
      participantState.isSharingScreen,
      setVideoTrack,
      setAudioTrack,
      update,
    ]
  );

  const handleFocusedTrackSubscribedEvent = useCallback(
    function (track: RemoteVideoTrack | RemoteAudioTrack ) {
      if (track.kind === "video") {
        if (/-screen/g.test(track.name) || /-program/g.test(track.name)) {
          (track as any).setPriority("high");
        } else {
          // Only screen shares and program feeds should be focused.
          return;
        }

        if (/-screen/g.test(track.name)) {
          setVideoTrack(track);
          if (!participantState.isSharingScreen) {
            update({ isSharingScreen: true });
          }
        } else {
          update({
            videoIsEnabled: track.isEnabled,
            isSwitchedOff: (track as any).isSwitchedOff,
          });
          setVideoTrack(track);
        }
      } else if (track.kind === "audio") {
        update({ audioIsEnabled: track.isEnabled });
        setAudioTrack(track);
      }
    },
    [participantState.isSharingScreen, setAudioTrack, setVideoTrack, update]
  );
  const handleRemoteTrackSubscribedEvent = useCallback(
    function (track: RemoteVideoTrack | RemoteAudioTrack ) {
      if (track.kind === "video") {
        // Screen shares and program feeds should not render
        // in the RemoteParticipant component.
        if (/-screen/g.test(track.name)) {
          if (!participantState.isSharingScreen) {
            update({ isSharingScreen: true });
          }
          return;
        } else if (/-program/g.test(track.name)) {
          return;
        }

        update({
          videoIsEnabled: track.isEnabled,
          isSwitchedOff: (track as any).isSwitchedOff,
        });
        setVideoTrack(track);
      } else if (track.kind === "audio") {
        update({ audioIsEnabled: track.isEnabled });
        setAudioTrack(track);
      }
    },
    [participantState.isSharingScreen, setVideoTrack, setAudioTrack, update]
  );

  const handleDataTrackSubscribedEvent = useCallback(
    function (track: RemoteDataTrack) {
      if (track.kind === "data") {
        track.on('message', data => {
          dispatch(
            addChatMessage({
              id: uuidv4(),
              sender: participantState.identity,
              message: data.toString()
            })
          );
        });
      }
    },
    [dispatch, participantState.identity]
  );

  const handleTrackUnsubscribedEvent = useCallback(
    function (track: RemoteVideoTrack | RemoteAudioTrack) {
      if (track.kind === "video") {
        if (/-screen/g.test(track.name)) {
          setVideoTrack(undefined);
          update({ isSharingScreen: false });
        } else {
          update({
            videoIsEnabled: track.isEnabled,
            isSwitchedOff: (track as any).isSwitchedOff,
          });
          setVideoTrack(undefined);
        }
      } else if (track.kind === "audio") {
        update({ audioIsEnabled: track.isEnabled });
        setAudioTrack(undefined);
      } //else data
    },
    [setVideoTrack, setAudioTrack, update]
  );

  const handleRemoteTrackEvents = useCallback(
    function (track: RemoteTrack) {
      track.on("disabled", () => {
        if (track.kind === "video") {
          update({ videoIsEnabled: false });
        } else {
          update({ audioIsEnabled: false });
        }
      });

      track.on("enabled", () => {
        if (track.kind === "video") {
          update({ videoIsEnabled: true });
        } else {
          update({ audioIsEnabled: true });
        }
      });

      track.on("switchedOff", () => {
        if (track.isEnabled) {
          update({ isSwitchedOff: true });
        }
      });

      track.on("switchedOn", () => {
        update({ isSwitchedOff: false });
      });
    },
    [update]
  );

  return {
    handleRemoteTrackEvents,
    handleTrackUnsubscribedEvent,
    handleRemoteTrackSubscribedEvent,
    handleDataTrackSubscribedEvent,
    handleFocusedTrackSubscribedEvent,
    setInitialTracks,
    setInitialFocusedParticipantTracks,
  };
}
